import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import Modal from "@/components/ui/modal";
import {
  FileText,
  ArrowLeft,
  Printer,
  Download,
  Tag,
  FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

const PDFModal = ({
  show,
  onHide,
  currentRepairData,
  isGeneratingPDF,
  handleGeneratePDFReport,
  onPrintJobNote,
  onPrintJobLabel,
  pdfBlobUrl,
  onClearPreview,
  currencySymbol = "$",
}) => {
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const [activeAction, setActiveAction] = React.useState(null);

  // Reset active action when loading finishes
  React.useEffect(() => {
    if (!isGeneratingPDF) {
      setActiveAction(null);
    }
  }, [isGeneratingPDF]);

  const handleActionClick = (type, fn) => {
    setActiveAction(type);
    fn(currentRepairData.id);
  };

  const handlePrint = () => {
    if (pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl, "_blank");
      if (printWindow) {
        printWindow.focus();
      }
    }
  };

  if (!currentRepairData) return null;

  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title={
        <div className="flex items-center gap-2">
          {pdfBlobUrl && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary"
              onClick={onClearPreview}
            >
              <ArrowLeft size={18} />
            </Button>
          )}
          <div className="flex flex-col">
            <span>Repair #{currentRepairData.repair_number}</span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-normal">
              Device: {currentRepairData.item_description}
            </span>
          </div>
        </div>
      }
      icon={FileText}
      maxWidth={pdfBlobUrl ? "max-w-4xl" : "max-w-md"}
      footer={
        pdfBlobUrl ? (
          <div className="flex justify-end gap-3 w-full">
            <Button variant="outline" onClick={onClearPreview}>
              Back to Options
            </Button>
            <Button variant="secondary" className="gap-2" onClick={handlePrint}>
              <Printer size={16} /> Print
            </Button>
            <Button asChild className="gap-2">
              <a
                href={pdfBlobUrl}
                download={`Repair-${currentRepairData.repair_number}.pdf`}
              >
                <Download size={16} /> Download
              </a>
            </Button>
          </div>
        ) : (
          <div className="flex justify-end w-full">
            <Button variant="outline" onClick={onHide}>
              Close
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {!pdfBlobUrl ? (
          <div className="flex flex-col items-center py-4">
            <div className="mb-6 text-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <h5 className="text-lg font-semibold">Generate Documents</h5>
              <p className="text-sm text-muted-foreground">
                Total Cost: {currencySymbol}
                {parseFloat(currentRepairData.total_cost || 0).toFixed(2)}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 w-full">
              {hasPermission("report repair invoice view") && (
                <Button
                  variant="outline"
                  className="h-auto flex items-center justify-between p-4 text-left border-2 hover:border-primary/50"
                  disabled={isGeneratingPDF}
                  onClick={() =>
                    handleActionClick("invoice", (id) =>
                      handleGeneratePDFReport(id, "invoice"),
                    )
                  }
                >
                  <div>
                    <span className="block font-bold">Repair Invoice</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      Official invoice with payment details
                    </span>
                  </div>
                  {isGeneratingPDF && activeAction === "invoice" ? (
                    <Spinner size={16} />
                  ) : (
                    <FileText className="h-5 w-5 opacity-40" />
                  )}
                </Button>
              )}

              {hasPermission("report repair job note view") && (
                <Button
                  variant="outline"
                  className="h-auto flex items-center justify-between p-4 text-left border-2 hover:border-primary/50"
                  disabled={isGeneratingPDF}
                  onClick={() =>
                    handleActionClick(
                      "note",
                      onPrintJobNote ||
                        ((id) => handleGeneratePDFReport(id, "job-note")),
                    )
                  }
                >
                  <div>
                    <span className="block font-bold">Repair Job Note</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      Internal or customer service document
                    </span>
                  </div>
                  {isGeneratingPDF && activeAction === "note" ? (
                    <Spinner size={16} />
                  ) : (
                    <FileDown className="h-5 w-5 opacity-40" />
                  )}
                </Button>
              )}

              {hasPermission("report repair job label view") && (
                <Button
                  variant="outline"
                  className="h-auto flex items-center justify-between p-4 text-left border-2 hover:border-primary/50"
                  disabled={isGeneratingPDF}
                  onClick={() =>
                    handleActionClick(
                      "label",
                      onPrintJobLabel ||
                        ((id) => handleGeneratePDFReport(id, "job-label")),
                    )
                  }
                >
                  <div>
                    <span className="block font-bold">Repair Job Label</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      Barcode label for the physical device
                    </span>
                  </div>
                  {isGeneratingPDF && activeAction === "label" ? (
                    <Spinner size={16} />
                  ) : (
                    <Tag className="h-5 w-5 opacity-40" />
                  )}
                </Button>
              )}
            </div>

            {isGeneratingPDF && (
              <Alert className="mt-6 bg-primary/5 border-primary/20">
                <Spinner size={14} className="mr-2" />
                <AlertDescription>
                  Generating document, please wait...
                </AlertDescription>
              </Alert>
            )}
          </div>
        ) : (
          <Card className="overflow-hidden border-2">
            <div className="pdf-viewer-container" style={{ height: "65vh" }}>
              <iframe
                src={`${pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                title="PDF Preview"
                width="100%"
                height="100%"
                className="border-none"
              />
            </div>
          </Card>
        )}
      </div>
    </Modal>
  );
};

PDFModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  currentRepairData: PropTypes.object,
  isGeneratingPDF: PropTypes.bool,
  handleGeneratePDFReport: PropTypes.func.isRequired,
  onPrintJobNote: PropTypes.func,
  onPrintJobLabel: PropTypes.func,
  pdfBlobUrl: PropTypes.string,
  onClearPreview: PropTypes.func,
  currencySymbol: PropTypes.string,
};

export default PDFModal;
